import { localizedStrings } from '@core/strings'
import {
  SelectFilterInternalArguments,
  SelectFilterValue,
  useSelectFilter,
} from './fundamentals'
import { useOrganisationCompanies } from '@core/react-query/features/company'
import { FilterHookArgumentsBase } from '@core/providers/filters'
import { useDebouncedValue } from '@core/utils/useDebouncedValue'
import { useState } from 'react'

type CompanyFilterHookArgs<Multiple extends boolean | undefined> =
  FilterHookArgumentsBase<SelectFilterValue<string, Multiple>> & {
    multiple?: Multiple
    organisationId: number
  }

export const useCompanyFilter: <Multiple extends boolean | undefined>(
  arg: CompanyFilterHookArgs<Multiple>,
) => SelectFilterInternalArguments<string, Multiple> = ({
  organisationId,
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = useState<string>()
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)

  const onInputChange = (input: string) => {
    if (!input) setSearchTerm('')
    else if (input.length < 3) return
    setSearchTerm(input)
  }

  const { data: companiesData } = useOrganisationCompanies({
    organisationId,
    page: 1,
    size: 1000,
    term: debouncedSearchTerm,
  })

  const companies = companiesData?.content || []

  return useSelectFilter({
    label: localizedStrings.company,
    nothingSelectedText: localizedStrings.all,
    options: companies.map((c) => String(c.id)),
    valueToLabel: (companyId) =>
      companies.find((c) => String(c.id) === companyId)?.name || companyId,
    onInputChange,
    ...rest,
  })
}
